import type { ICommand } from "./ICommand";

const MAX_HISTORY_SIZE = 10;

export type HistoryEventType = "change" | "undo" | "redo";
export type HistoryEventHandler = () => void;

/**
 * Manages undo/redo history stack.
 * Commands are executed and tracked for undo/redo operations.
 */
export class CommandHistory {
  private undoStack: ICommand[] = [];
  private redoStack: ICommand[] = [];
  private listeners = new Map<HistoryEventType, Set<HistoryEventHandler>>();
  /**
   * Édition verrouillée (fusion de versions en cours) : aucune commande n'est
   * exécutée, annulée ni rétablie. Filet de sécurité — l'interface bloque déjà
   * les outils et raccourcis d'édition.
   */
  private locked = false;

  setLocked(locked: boolean): void {
    this.locked = locked;
  }

  get isLocked(): boolean {
    return this.locked;
  }

  /**
   * Execute a command and add it to the undo stack.
   * Clears the redo stack since we're branching history.
   */
  execute(command: ICommand): void {
    // Pas de dispose() : l'appelant peut encore tenir les objets de la commande.
    if (this.locked) return;
    command.execute();
    this.undoStack.push(command);

    // Clear redo stack on new command (branching history)
    this.clearRedoStack();

    // Limit history size
    if (this.undoStack.length > MAX_HISTORY_SIZE) {
      const oldest = this.undoStack.shift();
      oldest?.dispose();
    }

    this.emit("change");
  }

  /**
   * Undo the last command.
   * @returns true if undo was performed, false if stack was empty
   */
  undo(): boolean {
    if (this.locked) return false;
    const command = this.undoStack.pop();
    if (!command) return false;

    command.undo();
    this.redoStack.push(command);

    // Limit redo stack size
    if (this.redoStack.length > MAX_HISTORY_SIZE) {
      const oldest = this.redoStack.shift();
      oldest?.dispose();
    }

    this.emit("undo");
    this.emit("change");
    return true;
  }

  /**
   * Redo the last undone command.
   * @returns true if redo was performed, false if stack was empty
   */
  redo(): boolean {
    if (this.locked) return false;
    const command = this.redoStack.pop();
    if (!command) return false;

    command.redo();
    this.undoStack.push(command);

    this.emit("redo");
    this.emit("change");
    return true;
  }

  /** Check if undo is available */
  get canUndo(): boolean {
    return this.undoStack.length > 0;
  }

  /** Check if redo is available */
  get canRedo(): boolean {
    return this.redoStack.length > 0;
  }

  /** Get the number of commands in the undo stack */
  get undoCount(): number {
    return this.undoStack.length;
  }

  /** Get the number of commands in the redo stack */
  get redoCount(): number {
    return this.redoStack.length;
  }

  /** Clear the redo stack, disposing all commands */
  private clearRedoStack(): void {
    for (const cmd of this.redoStack) {
      cmd.dispose();
    }
    this.redoStack = [];
  }

  /** Subscribe to history events */
  on(event: HistoryEventType, handler: HistoryEventHandler): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  /** Unsubscribe from history events */
  off(event: HistoryEventType, handler: HistoryEventHandler): void {
    this.listeners.get(event)?.delete(handler);
  }

  /** Mark the drawing as dirty without adding a command to the history stack. */
  markDirty(): void {
    this.emit("change");
  }

  /** Emit an event to all listeners */
  private emit(event: HistoryEventType): void {
    this.listeners.get(event)?.forEach((handler) => handler());
  }

  /** Clear all history and dispose all commands */
  clear(): void {
    for (const cmd of [...this.undoStack, ...this.redoStack]) {
      cmd.dispose();
    }
    this.undoStack = [];
    this.redoStack = [];
    this.emit("change");
  }

  /** Dispose the history manager */
  dispose(): void {
    this.clear();
    this.listeners.clear();
  }
}
